import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import containerService from '../../services/ContainerService.js';
import { Package, Pencil, ArrowLeft } from "lucide-react";

const ViewContainer = () => {
  const { containerId } = useParams();
  const [container, setContainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContainer();
  }, [containerId]);

  const fetchContainer = async () => {
    try {
      const response = await containerService.getById(containerId);
      setContainer(response.data);
    } catch (err) {
      Alert.error('Failed to load container details');
      console.error(err);
      navigate('/containers');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading container details...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!container) {
    return null;
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Package className="w-5 h-5 text-buttonBg" />Container Details
                    </h1>
                  </div>
                    <div className='w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3'>
                    {/* Action Buttons */}
                        <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3">
                          <button
                            onClick={() => navigate(`/containers/edit/${containerId}`)}
                            className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					            {/* ICON */}
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />
                      <span className="relative z-10">Edit Container</span>
                            
                          </button>
                          <button
                            onClick={() => navigate('/containers')}
                            className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <ArrowLeft
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Back to Containers</span>
                            
                          </button>
                        </div>
                      </div>
                  
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {/* Profile Header Section */}
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg md:text-lg font-semibold text-secondary mb-0 break-words">
                          {container.container_no || 'Container'}
                        </h2>
                      </div>
                    </div>
                  </div>

                  {/* Details Sections */}
                  <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    {/* Basic Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Container No</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.container_no || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Container Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.container_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manufacturer</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.manufacturer || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Manufacture Date</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(container.manufacture_date)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Serial Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.serial_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Size (Feet)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.size || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Radius</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.radius || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Capacity (Ltr)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.capacity_ltr || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Classification & Identification */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Classification & Identification
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">ISO Type</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.iso_type || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">IMO Class</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.imo_class || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">UN Number</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.un_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.csc || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">CSC Validity</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.csc_validity || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Weight & Pressure */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Weight & Pressure
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Maximum Gross Weight (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.maximum_gross_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Tare Weight (kg)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.tare_weight || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">MAWP (Bar)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.mawp || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Test Pressure</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.test_pressure || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Location & Service */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Location & Service
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Cleaning Corporation</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.cleaning_corporation || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Depot Name</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.depot_name || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">LED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(container.led)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">NED</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(container.ned)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Discharge & Steam */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Discharge & Steam
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Top Discharge</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {container.top_discharge === 'yes' ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Syphon Tube</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                            {container.syphon_tube === 'yes' ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Steam Tube WP (Bar)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.steam_tube_wp || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Steam Test Pressure (Bar)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.steam_test_pressure || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Material Specifications */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Material Specifications
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Shell Material</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.shell_material || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Shell Thickness (mm)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.shell_thikness || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Head Material</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.head_material || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Head Thickness (mm)</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.head_thikness || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* System Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Container ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Client ID</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.client_id}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created By</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{container.created_by || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Created At</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(container.created_at)}</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">Last Updated</label>
                          <p className="text-sm sm:text-base text-secondary font-semibold break-all">{formatDate(container.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewContainer;